import { playerUsageFromContext, rootChipBudgetBreakdown } from './playerChipAccounting';
import { countingVersion } from './counting';
import { fixedChipCells } from './chips';
import type Context from '@/context';
import type { ICircuitModel, ITileManagerModel } from '@/interfaces/models';
import type { TileInstance } from '@/models/tiles/TileInstance';
import { CircuitType } from '@/schemas/common';
import { TileType } from '@/models/TileTypes';
import { terminalPosition, isPlaceableInterface, type Challenge } from './definition';

export function challengeForCircuit(context: Context, circuit: ICircuitModel) {
  return context.challenge && circuit === context.mainChip?.logicCircuit.circuitModel
    ? context.challenge
    : undefined;
}
/** Only the active root Logic canvas has a positioned challenge boundary. */
export function allowsChallengePlacement(
  context: Context,
  manager: ITileManagerModel,
  point: { x: number; y: number },
) {
  const challenge = context.challenge;
  if (
    !challenge?.buildOrigin ||
    manager !== context.mainChip?.logicCircuit.circuitModel.tileManagerModel
  )
    return true;
  const { x, y } = challenge.buildOrigin;
  return (
    point.x >= x &&
    point.y >= y &&
    point.x < x + challenge.limits.width &&
    point.y < y + challenge.limits.height
  );
}
export function fixedCells(challenge: Challenge) {
  return [
    ...(['inputs', 'outputs'] as const).flatMap((kind) =>
      challenge[kind].flatMap((label, i) => {
        const position = terminalPosition(challenge, kind, i);
        return position
          ? [
              {
                ...position,
                type: TileType.SingleInterface,
                label,
                input: kind === 'inputs',
                delayMs: undefined,
              },
            ]
          : [];
      }),
    ),
    ...(challenge.fixedTiles ?? []).map((t) => ({
      ...t,
      label: undefined,
      input: false,
    })),
  ];
}
export function isFixedTile(tile: TileInstance): boolean {
  const context = tile.context,
    challenge = context?.challenge;
  if (
    !challenge ||
    tile.globalTilePosition.chipInstanceId !== context.mainChip.editingInstance.id
  )
    return false;
  if (tile.globalTilePosition.circuitType === CircuitType.ChipLogic) {
    // Legacy attempts may already have moved a named terminal. Keep it locked
    // by identity too; evaluation still reports its original required position.
    if (
      context.mainChip.interfaceLinks.some(
        (link) =>
          !isPlaceableInterface(challenge, link.label) &&
          (challenge.inputs.includes(link.label) ||
            challenge.outputs.includes(link.label)) &&
          link.tiles[CircuitType.ChipLogic]?.id === tile.id,
      )
    )
      return true;
    return [...fixedCells(challenge), ...fixedChipCells(challenge)].some(
      (c) => c.x === tile.location.x && c.y === tile.location.y,
    );
  }
  // Changing a paired Structure interface must not rename/reconfigure a fixed pin.
  const linkId = (
    tile.state as { connectedInterface?: { interfaceLinkId?: string } } | null
  )?.connectedInterface?.interfaceLinkId;
  return (
    !!linkId &&
    context.mainChip.interfaceLinks.some(
      (link) =>
        link.id === linkId &&
        !!link.tiles[CircuitType.ChipLogic] &&
        isFixedTile(link.tiles[CircuitType.ChipLogic]!),
    )
  );
}
/** Challenge interfaces are supplied terminals or authored chip ports, not player primitives. */
export function isSuppliedChallengeInterface(type: TileType) {
  return type === TileType.SingleInterface || type === TileType.MultiInterface;
}

export function tileBudget(context: Context, circuit: ICircuitModel, type: TileType) {
  if (
    context.challenge?.playerChips &&
    circuit !== context.mainChip?.logicCircuit.circuitModel
  )
    return undefined;
  if (
    context.challenge &&
    !context.challengeAuthoring &&
    isSuppliedChallengeInterface(type)
  )
    return { allowed: false, remaining: 0, maximum: 0 };
  const challenge = challengeForCircuit(context, circuit);
  if (!challenge) return undefined;
  const rule = challenge.limits.tiles.find((t) => t.type === type);
  let used = circuit.tiles.filter(
    (t) =>
      !t.meta.partOfChip &&
      t.tileType === type &&
      (countingVersion(challenge) === 1 || !isFixedTile(t)),
  ).length;
  let breakdown: ReturnType<typeof rootChipBudgetBreakdown> | undefined;
  if (challenge.playerChips) {
    try {
      used = playerUsageFromContext(context).counts[type] ?? 0;
      if (rule?.max !== undefined && used > rule.max)
        breakdown = rootChipBudgetBreakdown(context, type);
    } catch {
      /* Invalid draft is explained at the root. */
    }
  }
  return {
    allowed: !!rule && rule.available !== false,
    remaining:
      rule && rule.available !== false
        ? rule.max === undefined
          ? undefined
          : rule.max - used
        : 0,
    maximum: rule?.available === false ? 0 : rule?.max,
    breakdown,
  };
}

export function allowsChallengeTile(
  context: Context,
  manager: ITileManagerModel,
  type: TileType,
  state?: unknown,
) {
  const challenge = context.challenge;
  if (
    challenge?.playerChips &&
    manager !== context.mainChip?.logicCircuit.circuitModel.tileManagerModel
  )
    return true;
  if (challenge && isSuppliedChallengeInterface(type))
    return (
      !!context.challengeAuthoring ||
      isPendingChallengeInterface(context, manager, type, state)
    );
  if (
    !challenge ||
    manager !== context.mainChip?.logicCircuit.circuitModel.tileManagerModel
  )
    return true;
  return challenge.limits.tiles.some(
    (rule) => rule.type === type && rule.available !== false,
  );
}

/** Placement is restricted by named identity, not a general Single Interface allowance. */
export function isPendingChallengeInterface(
  context: Context,
  manager: ITileManagerModel,
  type: TileType,
  state: unknown,
) {
  const challenge = context.challenge;
  if (
    !challenge ||
    type !== TileType.SingleInterface ||
    manager !== context.mainChip.logicCircuit.circuitModel.tileManagerModel
  )
    return false;
  const id = (state as { connectedInterface?: { interfaceLinkId?: string } } | undefined)
    ?.connectedInterface?.interfaceLinkId;
  const link = context.mainChip.interfaceLinks.find((entry) => entry.id === id);
  return (
    !!link &&
    isPlaceableInterface(challenge, link.label) &&
    !link.tiles[CircuitType.ChipLogic]
  );
}
export function missingChallengeInterfaces(context: Context, challenge: Challenge) {
  return [...challenge.inputs, ...challenge.outputs].filter(
    (name) =>
      !context.mainChip.interfaceLinks.some(
        (link) => link.label === name && link.tiles[CircuitType.ChipLogic],
      ),
  );
}

/** Required signal identity/mode stays authored even when its location is movable. */
export function isNamedChallengeInterface(tile: TileInstance) {
  const context = tile.context;
  return (
    !!context?.challenge &&
    tile.globalTilePosition.chipInstanceId === context.mainChip.editingInstance.id &&
    context.mainChip.interfaceLinks.some(
      (link) =>
        (context.challenge!.inputs.includes(link.label) ||
          context.challenge!.outputs.includes(link.label)) &&
        Object.values(link.tiles).some((entry) => entry?.id === tile.id),
    )
  );
}
